// Tap someone in the world: their card. Text them, invite them over, say gm, or follow.
import { dispatch, gameState, openApp, personCard, playerLoc, showToast } from "../bridge";
import { invite, islandPlayers, offerRide, wave } from "../../net/island";
import { session } from "../../net/session";
import { compact } from "../format";
import { PfpImage } from "../PfpImage";
import { generatePfpTraits } from "@degen/sim/pfp/traits";
import { house } from "@degen/sim/houses/houses";

const BLURB: Record<string, string> = {
  alpha: "posts calls, sometimes right", doomer: "thinks it's all going to zero", shillbot: "will shill anything for a bag", sleuth: "onchain detective",
  influencer: "big reach, bigger ego", builder: "actually ships things", memelord: "meme first, ask later",
};

export function PersonCard() {
  const id = personCard.value;
  const s = gameState();
  if (!id || !s) return null;
  if (id.startsWith("p:")) return <RealPersonCard id={id.slice(2)} />;
  const acc = s.social.accounts.find((a) => a.id === id);
  if (!acc) return null;
  const close = () => (personCard.value = null);
  const following = s.social.following.includes(id);
  const thread = s.messages.threads[id];
  const home = s.houses.homeId ? house(s.houses.homeId) : null;
  const text = (msg: string, toastMsg: string) => {
    if (dispatch({ type: "SEND_MESSAGE", contactId: id, text: msg }).ok) showToast(toastMsg);
  };
  return (
    <div class="modal-backdrop person-backdrop" onClick={close}>
      <div class="modal person-card" role="dialog" aria-label={`@${acc.handle}`} onClick={(e) => e.stopPropagation()}>
        <div class="person-head">
          <PfpImage seed={acc.pfpSeed} traits={generatePfpTraits(acc.pfpSeed)} size={64} />
          <div>
            <b class="big">{acc.name}</b>
            <div class="muted small">@{acc.handle}</div>
            <div class="small">
              {compact(acc.followers)} followers · {BLURB[acc.personality] ?? acc.personality}
            </div>
            {thread && <div class="muted small">Friendship {Math.round(thread.relationship)}/100</div>}
          </div>
        </div>
        <div class="person-actions">
          <button class="btn primary" onClick={() => (close(), openApp("messages", { chat: id }))}>
            💬 Text
          </button>
          <button class="btn" disabled={!home} onClick={() => (text("come over to my place later? 🏠", `🏠 Invite sent to @${acc.handle}. Their reply lands in Messages.`), close())}>
            🏠 Invite over
          </button>
          <button class="btn" onClick={() => (text("gm ☀️", `👋 You said gm to @${acc.handle}.`), close())}>
            👋 Say gm
          </button>
          <button class="btn ghost" disabled={following} onClick={() => dispatch({ type: "FOLLOW", accountId: id }).ok && showToast(`Following @${acc.handle}`)}>
            {following ? "✓ Following" : "➕ Follow"}
          </button>
        </div>
        {home && <p class="fine">Invites go to your {home.name}. Better friendship and a better-decorated place = more yeses.</p>}
        <button class="link small" onClick={close}>
          Close
        </button>
      </div>
    </div>
  );
}

/** A real player on the island (live). Calm, simple actions: wave, offer a ride, invite over. */
function RealPersonCard({ id }: { id: string }) {
  const p = islandPlayers.value.get(id);
  const close = () => (personCard.value = null);
  if (!p) {
    close();
    return null;
  }
  const loc = playerLoc.value;
  const s = gameState();
  const home = s?.houses.homeId ? house(s.houses.homeId) : null;
  const canInvite = !!home && !!session.value;
  return (
    <div class="modal-backdrop person-backdrop" onClick={close}>
      <div class="modal person-card" role="dialog" aria-label={`@${p.handle}`} onClick={(e) => e.stopPropagation()}>
        <div class="person-head">
          <span class="real-badge" aria-hidden="true">
            {p.handle.slice(0, 1).toUpperCase()}
          </span>
          <div>
            <b class="big">@{p.handle}</b>
            <div class="muted small">{p.verified ? "✓ Real player" : "Real player · guest"}</div>
          </div>
        </div>
        <div class="person-actions">
          <button class="btn primary" onClick={() => (wave(), close())}>
            👋 Wave
          </button>
          {loc.driving && !loc.highway && (
            <button
              class="btn"
              onClick={() => {
                const err = offerRide(id);
                showToast(err ?? `🚗 Ride offer sent to @${p.handle}. Waiting for them to hop in…`);
                if (!err) close();
              }}
            >
              🚗 Offer a ride ({loc.riders.length}/{loc.seats})
            </button>
          )}
          <button
            class="btn"
            disabled={!canInvite}
            onClick={() => {
              invite(id);
              showToast(`🏠 Invite sent to @${p.handle}.`);
              close();
            }}
          >
            🏠 Invite over
          </button>
        </div>
        {!canInvite && <p class="fine">{!session.value ? "Sign in (Settings) to invite people to your place." : "Get a home first to invite people over."}</p>}
        <button class="link small" onClick={close}>
          Close
        </button>
      </div>
    </div>
  );
}
